import { useToast } from '@chakra-ui/react';
import {
  articlesApi,
  UpdateArticleCertificatesRequest,
} from '@texas/api/endpoints/articlesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { usePendingChanges } from '@texas/hooks/usePendingChanges';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { articleManageEvents } from '../events';
import { customersApi } from '@texas/api/endpoints/customersApi';
import { customerComplianceDocumentEvents } from '@texas/components/customer/complianceDocuments/events';

export function useArticleCertificates(articleId: number, customerId: number) {
  const { t } = useTranslation();
  const toast = useToast();
  const customerRequirements = useApiResource(
    customersApi.getComplianceDocuments,
  );
  const { refetch: refetchCustomerRequirements } = customerRequirements;
  const {
    branchRegionsData: [regions, getRegions, regionsLoading, , regionsError],
    complianceDocumentDefinitionsData: [
      definitions,
      getDefinitions,
      definitionsLoading,
      ,
      definitionsError,
    ],
  } = useMetadata();
  const {
    data,
    refetch,
    set,
    loading: branchesLoading,
    error: branchesError,
  } = useApiResource(articlesApi.getCertificates);
  const {
    request,
    loading: saving,
    error: saveError,
    iteration,
  } = useApiRequest(articlesApi.updateCertificates);
  const mutation = useMemo(
    () => ({ request, loading: saving, error: saveError, iteration }),
    [request, saving, saveError, iteration],
  );
  const form = useForm<UpdateArticleCertificatesRequest>({
    defaultValues: { branches: [] },
  });
  const { handleSubmit, reset } = form;
  const { resetPendingChanges, markChangesSaved, hasPendingChanges } =
    usePendingChanges(form);
  const values = useWatch({ control: form.control, name: 'branches' });
  const [regionId, setRegionId] = useState('');
  const resources = useMemo(
    () => ({
      customerRequirements,
      branches: {
        data,
        refetch,
        set,
        loading: branchesLoading,
        error: branchesError,
      },
      regions: { data: regions, loading: regionsLoading, error: regionsError },
      definitions: {
        data: definitions,
        loading: definitionsLoading,
        error: definitionsError,
      },
    }),
    [
      customerRequirements,
      data,
      refetch,
      set,
      branchesLoading,
      branchesError,
      regions,
      regionsLoading,
      regionsError,
      definitions,
      definitionsLoading,
      definitionsError,
    ],
  );
  const { loading, error } = useMemo(
    () => ({
      loading: Object.values(resources).some((resource) => resource.loading),
      error: Object.values(resources).find((resource) => resource.error)?.error,
    }),
    [resources],
  );
  const changedBranches = useMemo(
    () =>
      values.filter((branch) => {
        const original = data?.find(
          (item) => item.branchId === branch.branchId,
        );
        return (
          original &&
          (original.certificateDefinitionIds.length !==
            branch.certificateDefinitionIds.length ||
            original.certificateDefinitionIds.some(
              (id) => !branch.certificateDefinitionIds.includes(id),
            ))
        );
      }),
    [data, values],
  );

  useEffect(() => {
    getRegions('');
    getDefinitions({
      searchTerm: '',
      includeArchived: true,
      sortBy: 'name',
      sortDesc: false,
    });
  }, [getRegions, getDefinitions]);

  useEffect(() => {
    void refetch(articleId);
    return articleManageEvents.certificatesModified.sub((id) => {
      if (id === articleId) void refetch(articleId);
    });
  }, [articleId, refetch]);

  useEffect(() => {
    void refetchCustomerRequirements(customerId);
    return customerComplianceDocumentEvents.changed.subscribe((id) => {
      if (id === customerId) void refetchCustomerRequirements(customerId);
    });
  }, [customerId, refetchCustomerRequirements]);

  useEffect(() => {
    if (data) {
      resetPendingChanges({
        branches: data.map(({ branchId, certificateDefinitionIds }) => ({
          branchId,
          certificateDefinitionIds: [...certificateDefinitionIds].sort(
            (a, b) => a - b,
          ),
        })),
      });
    }
  }, [data, resetPendingChanges]);

  const save = useMemo(
    () =>
      handleSubmit(async (submitted) => {
        if (loading || error || saving || !changedBranches.length) return;
        const response = await request(articleId, {
          branches: changedBranches,
        });
        if (response.hasError) return;
        markChangesSaved(submitted);
        toast({
          title: t('article.management.certificatesSaved'),
          status: 'success',
          position: 'bottom-right',
        });
        articleManageEvents.certificatesModified.dispatch(articleId);
      }),
    [
      handleSubmit,
      loading,
      error,
      saving,
      changedBranches,
      request,
      articleId,
      markChangesSaved,
      toast,
      t,
    ],
  );
  const availableRegions = useMemo(
    () =>
      regions.filter((region) =>
        region.branches.some((branch) =>
          data?.some((item) => item.branchId === branch.id),
        ),
      ),
    [regions, data],
  );
  const visibleBranches = useMemo(() => {
    const selectedRegion = regions.find(
      (region) => String(region.id) === regionId,
    );
    return (data ?? []).filter(
      (branch) =>
        !selectedRegion ||
        selectedRegion.branches.some((item) => item.id === branch.branchId),
    );
  }, [data, regions, regionId]);

  return {
    form,
    resources,
    visibleBranches,
    availableRegions,
    regionId,
    setRegionId,
    loading,
    error,
    mutation,
    save,
    hasPendingChanges,
    changedBranchCount: changedBranches.length,
    discard: reset,
  };
}
